import React, { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { getQueueAIFiles } from "../../api/queueAI";
import { useDatabase } from "./DatabaseContext";
import { ReactComponent as ClockIcon } from "../../assets/images/time-line.svg";
import { ReactComponent as ErrorIcon } from "../../assets/images/error-warning-line.svg";
import { ReactComponent as CheckIcon } from "../../assets/images/check-line.svg";
import qs from "./QueueStatus.module.css";

interface QueueStatusProps {
    companyId?: string;
    compact?: boolean;
}

interface QueueFile {
    id: string;
    status: string;
    createdAt: string;
    error?: string;
}

interface QueueStatusData {
    pending: number;
    processing: number;
    completed: number;
    failed: number;
    total: number;
}

const QueueStatus: React.FC<QueueStatusProps> = ({ companyId, compact = false }) => {
    const { t: rawT } = useTranslation();
    const t = rawT as any;
    const { queueRefreshTrigger, refreshFiles, hasActiveVectorSearch } = useDatabase();

    // Use ref to always have the latest refreshFiles function and vector search state
    const refreshFilesRef = useRef(refreshFiles);
    const hasActiveVectorSearchRef = useRef(hasActiveVectorSearch);
    useEffect(() => {
        refreshFilesRef.current = refreshFiles;
        hasActiveVectorSearchRef.current = hasActiveVectorSearch;
    }, [refreshFiles, hasActiveVectorSearch]);

    const [queueData, setQueueData] = useState<QueueStatusData>({
        pending: 0,
        processing: 0,
        completed: 0,
        failed: 0,
        total: 0,
    });
    const [previousQueueData, setPreviousQueueData] = useState<QueueStatusData>({
        pending: 0,
        processing: 0,
        completed: 0,
        failed: 0,
        total: 0,
    });
    const [error, setError] = useState<string | null>(null);

    const fetchQueueStatus = async () => {
        const token = localStorage.getItem("token");
        if (!token) return;

        setError(null);

        try {
            const response = await getQueueAIFiles({
                token,
                page: 1,
                limit: 1000, // Get all files to count statuses
                companyId,
            });

            if (response.data) {
                const files: QueueFile[] = response.data;

                const statusCounts = files.reduce(
                    (acc, file) => {
                        switch (file.status) {
                            case "queued":
                                acc.pending++;
                                break;
                            case "queuedtest":
                                acc.pending++;
                                break;
                            case "in_progress":
                                acc.processing++;
                                break;
                            case "completed":
                                acc.completed++;
                                break;
                            case "failed":
                            case "error":
                                acc.failed++;
                                break;
                        }
                        acc.total++;
                        return acc;
                    },
                    { pending: 0, processing: 0, completed: 0, failed: 0, total: 0 },
                );

                // Only update if data actually changed to prevent blinking
                setQueueData((prevData) => {
                    const hasChanged =
                        prevData.pending !== statusCounts.pending ||
                        prevData.processing !== statusCounts.processing ||
                        prevData.completed !== statusCounts.completed ||
                        prevData.failed !== statusCounts.failed ||
                        prevData.total !== statusCounts.total;

                    if (hasChanged) {
                        return statusCounts;
                    }

                    return prevData;
                });

                // Check if files were completed and trigger refresh outside of setState
                const newCompleted = statusCounts.completed;
                const previousCompleted = previousQueueData.completed;

                if (newCompleted > previousCompleted) {
                    // Files were completed, refresh files list
                    // Skip auto-refresh if there's an active vector search (to avoid expensive queries)
                    if (!hasActiveVectorSearchRef.current) {
                        refreshFilesRef.current();
                    }
                }

                // Update previous data for next comparison
                setPreviousQueueData(statusCounts);
            }
        } catch (err) {
            console.error("Error fetching queue status:", err);
            setError(t("errorFetchingQueueStatus") || "Error fetching queue status");
        }
    };

    useEffect(() => {
        fetchQueueStatus();

        // Refresh every 10 seconds
        const interval = setInterval(fetchQueueStatus, 10000);
        return () => clearInterval(interval);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [companyId, queueRefreshTrigger]); // fetchQueueStatus recreated on every render, but we only want to restart interval on these deps

    if (error) {
        return <div className={qs.error}>{error}</div>;
    }

    return (
        <div className={qs.wrap}>
            {!compact && (
                <div className={qs.header}>
                    <span className={qs.title}>{t("processingQueue") || "Processing"}</span>
                </div>
            )}
            <div className={qs.items}>
                {queueData.pending > 0 && (
                    <div className={`${qs.item} ${qs.itemPending}`}>
                        <ClockIcon className={qs.icon} />
                        <span className={qs.count}>{queueData.pending}</span>
                    </div>
                )}

                {queueData.processing > 0 && (
                    <div className={`${qs.item} ${qs.itemProcessing}`}>
                        <ClockIcon className={qs.icon} />
                        <span className={qs.count}>{queueData.processing}</span>
                    </div>
                )}

                {queueData.failed > 0 && (
                    <div className={`${qs.item} ${qs.itemFailed}`}>
                        <ErrorIcon className={qs.icon} />
                        <span className={qs.count}>{queueData.failed}</span>
                    </div>
                )}

                {queueData.completed > 0 && (
                    <div className={`${qs.item} ${qs.itemCompleted}`}>
                        <CheckIcon className={qs.icon} />
                        <span className={qs.count}>{queueData.completed}</span>
                    </div>
                )}
            </div>
        </div>
    );
};

export default QueueStatus;
